import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Grid, Box, Alert, Typography } from "@mui/material";
import { Clear, CheckCircle } from "@mui/icons-material";
import { useAuthentication, useSnack } from "hooks";
import { RESET_PASSWORD_ERROR_MESSAGES, SIGN_IN_ERROR_MESSAGES } from "constants";
import { BasePage, LinkButton, LoadingButton, EmailInput, PasswordInput } from "components";
import { authenticationService } from "services";

const VERIFY_TIMEOUT_MS = 15000;

export function ResetPasswordPage() {
    const { user, isButtonLoading, verifyResetCode, resetPassword, loading } = useAuthentication();
    const { openSnack } = useSnack();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Read oobCode from URL
    const oobCodeFromParams = searchParams.get("oobCode");
    const oobCodeFromURL = new URLSearchParams(window.location.search).get("oobCode");
    const oobCode = oobCodeFromParams || oobCodeFromURL || "";

    // Determine mode based on auth state and URL params
    const isChangePasswordMode = !oobCode && user?.uid;

    // Lifecycle guards
    const startedVerifyRef = useRef(false);
    const resetConsumedRef = useRef(false);
    const mountedRef = useRef(false);

    // UI state
    const [phase, setPhase] = useState("init");
    const [email, setEmail] = useState("");
    const [verifyError, setVerifyError] = useState("");

    // Form state - now includes current password for change mode
    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [validation, setValidation] = useState({ 
        current: false, 
        password: false, 
        confirm: false 
    });

    // Submit feedback
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Page title and messaging based on mode
    const pageTitle = isChangePasswordMode ? "Change password" : "Reset password";
    const submitButtonText = isChangePasswordMode ? "Update password" : "Reset password";

    // Helpers
    const cleanCode = (code) => String(code).replace(/['"]/g, "").trim();

    const stripResetParamsFromUrl = (done = true) => {
        const url = new URL(window.location.href);
        url.searchParams.delete("mode");
        url.searchParams.delete("oobCode");
        url.searchParams.delete("apiKey");
        url.searchParams.delete("lang");
        if (done) url.searchParams.set("done", "1");
        window.history.replaceState({}, "", url.toString());
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Handle change password mode (signed in user, no oobCode)
    useEffect(() => {
        if (loading) return;

        if (isChangePasswordMode) {
            setPhase("change-password");
            setEmail(user?.email || "");
            return;
        }
    }, [loading, isChangePasswordMode, user]);

    // Handle reset password mode (oobCode present) - existing logic
    useEffect(() => {
        if (loading) {
            setPhase("init");
            return;
        }

        if (isChangePasswordMode) return; // Skip if in change password mode

        if (!oobCode) {
            setPhase("invalid");
            setVerifyError("Invalid reset link - no verification code found.");
            return;
        }

        if (startedVerifyRef.current || resetConsumedRef.current) {
            return;
        }

        startedVerifyRef.current = true;
        setPhase("verifying");
        setVerifyError("");

        const code = cleanCode(oobCode);
        let timeoutId = setTimeout(() => {
            if (!mountedRef.current || resetConsumedRef.current) return;
            setPhase("invalid");
            setVerifyError("Network timeout while verifying link. Please try again or request a new link.");
        }, VERIFY_TIMEOUT_MS);

        (async () => {
            try {
                const emailAddr = await verifyResetCode(code);
                if (!mountedRef.current) return;
                setEmail(emailAddr || "");
                setPhase("verified");
            } catch (err) {
                if (!mountedRef.current || resetConsumedRef.current) return;
                setPhase("invalid");
                const message = (err?.code && RESET_PASSWORD_ERROR_MESSAGES[err.code]) ||
                    `This reset link is invalid or has expired. (${err?.code || "Unknown error"})`;
                setVerifyError(message);
            } finally {
                clearTimeout(timeoutId);
            }
        })();
    }, [loading, oobCode, verifyResetCode, isChangePasswordMode]);

    // Validation handlers
    const handleCurrentPasswordValidation = useCallback((isValid) => {
        setValidation((v) => ({ ...v, current: isValid }));
    }, []);

    const handlePasswordValidation = useCallback((isValid) => {
        setValidation((v) => ({ ...v, password: isValid }));
    }, []);

    const handleConfirmValidation = useCallback((isValid) => {
        setValidation((v) => ({ ...v, confirm: isValid }));
    }, []);

    // Password change handlers
    const handleCurrentPasswordChange = (currentPassword) => {
        if (submitError) setSubmitError("");
        setCurrentPassword(currentPassword);
    };

    const handlePasswordChange = (password) => {
        if (submitError) setSubmitError("");
        setPassword(password);
    };

    const handleConfirmChange = (confirm) => {
        if (submitError) setSubmitError("");
        setConfirm(confirm);
    };

    // Custom validation for new password (can't be same as current in change mode)
    const validateNewPassword = useCallback((value) => {
        if (isChangePasswordMode) {
            const trimmed = value.trim();
            if (currentPassword.trim() && trimmed === currentPassword.trim()) {
                return "New password must be different from current password.";
            }
        }
        return "";
    }, [isChangePasswordMode, currentPassword]);

    // Custom validation for confirm password
    const validateConfirmPassword = useCallback((value) => {
        const trimmed = value.trim();
        if (!trimmed) return "Please confirm your new password.";
        if (trimmed !== password.trim()) return "Passwords do not match.";
        return "";
    }, [password]);

    // Form validation based on mode
    const isFormValid = useMemo(() => {
        if (isChangePasswordMode) {
            return validation.current && validation.password && validation.confirm &&
                   currentPassword.trim() && password.trim() && confirm.trim();
        }
        return validation.password && validation.confirm;
    }, [isChangePasswordMode, validation, currentPassword, password, confirm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitSuccess("");

        if (!isFormValid) return;

        try {
            if (isChangePasswordMode) {
                // Change password for signed-in user
                setIsChangingPassword(true);
                await authenticationService.changePassword(
                    currentPassword.trim(),
                    password.trim()
                );
                if (!mountedRef.current) return;
                
                setSubmitSuccess("Password updated successfully. Redirecting you back...");
                openSnack("Password updated successfully", "success");
                setIsRedirecting(true);
                
                // Navigate back to settings after success
                setTimeout(() => {
                    if (mountedRef.current) {
                        navigate("/settings");
                    }
                }, 2000);
            } else {
                // Reset password via email link
                const code = cleanCode(oobCode);
                await resetPassword(code, password.trim());
                if (!mountedRef.current) return;
                setSubmitSuccess("Password updated and email verified. You can now sign in with your new password.");
                resetConsumedRef.current = true;
                stripResetParamsFromUrl(true);
            }
        } catch (err) {
            if (!mountedRef.current) return;
            
            let message;
            if (isChangePasswordMode) {
                message = (err?.code && SIGN_IN_ERROR_MESSAGES[err.code]) ||
                         "An unexpected error occurred. Please try again.";
            } else {
                message = (err?.code && RESET_PASSWORD_ERROR_MESSAGES[err.code]) ||
                         `Could not update your password. Please try again. (${err?.code || err?.message})`;
            }
            setSubmitError(message);
        } finally {
            if (isChangePasswordMode) {
                setIsChangingPassword(false);
            }
        }
    };

    // Loading state
    if (phase === "init") {
        return (
            <BasePage title={pageTitle}>
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography>Initializing…</Typography>
                </Box>
            </BasePage>
        );
    }

    return (
        <BasePage title={pageTitle}>
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    {/* Reset password mode - invalid link */}
                    {phase === "invalid" && verifyError && (
                        <Grid size={12}>
                            <Alert icon={<Clear fontSize="inherit" />} severity="error">
                                {verifyError}
                            </Alert>
                            <Box sx={{ mt: 2, textAlign: "center" }}>
                                <LinkButton to="/forgot-password">Request a new link</LinkButton>
                            </Box>
                        </Grid>
                    )}

                    {/* Reset password mode - verifying link */}
                    {phase === "verifying" && (
                        <Grid size={12}>
                            <Box sx={{ textAlign: "center", py: 2 }}>
                                <Typography>Processing reset link…</Typography>
                            </Box>
                        </Grid>
                    )}

                    {/* Change password mode OR Reset password mode - verified */}
                    {(phase === "change-password" || phase === "verified") && (
                        <>
                            {/* Email field (disabled, shows context) */}
                            <Grid size={12}>
                                <EmailInput
                                    value={email}
                                    onChange={() => { }} // No-op since it's disabled
                                    disabled
                                />
                            </Grid>

                            {/* Current password (only in change password mode) */}
                            {isChangePasswordMode && (
                                <Grid size={12}>
                                    <PasswordInput
                                        value={currentPassword}
                                        onChange={handleCurrentPasswordChange}
                                        onValidationChange={handleCurrentPasswordValidation}
                                        label="Current password"
                                        autoComplete="current-password"
                                        enforceMinLength={false} // Don't enforce min length for current password
                                        disabled={isRedirecting}
                                    />
                                </Grid>
                            )}

                            {/* New password */}
                            <Grid size={12}>
                                <PasswordInput
                                    value={password}
                                    onChange={handlePasswordChange}
                                    onValidationChange={handlePasswordValidation}
                                    label="New password"
                                    autoComplete="new-password"
                                    enforceMinLength={true}
                                    validate={validateNewPassword}
                                    disabled={isRedirecting}
                                />
                            </Grid>

                            {/* Confirm new password */}
                            <Grid size={12}>
                                <PasswordInput
                                    value={confirm}
                                    onChange={handleConfirmChange}
                                    onValidationChange={handleConfirmValidation}
                                    label="Confirm new password"
                                    autoComplete="new-password"
                                    validate={validateConfirmPassword}
                                    disabled={isRedirecting}
                                />
                            </Grid>

                            {/* Error feedback */}
                            {submitError && (
                                <Grid size={12}>
                                    <Alert icon={<Clear fontSize="inherit" />} severity="error">
                                        {submitError}
                                    </Alert>
                                </Grid>
                            )}

                            {/* Success feedback */}
                            {submitSuccess && (
                                <Grid size={12}>
                                    <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
                                        {submitSuccess}
                                    </Alert>
                                </Grid>
                            )}

                            {/* Submit button */}
                            <Grid size={12}>
                                {isRedirecting ? (
                                    <LoadingButton
                                        fullWidth
                                        variant="contained"
                                        disabled
                                    >
                                        Redirecting...
                                    </LoadingButton>
                                ) : (
                                    <LoadingButton
                                        type="submit"
                                        loading={isChangePasswordMode ? isChangingPassword || !!submitSuccess : isButtonLoading}
                                        disabled={!isFormValid}
                                    >
                                        {submitButtonText}
                                    </LoadingButton>
                                )}
                            </Grid>

                            {/* Navigation links */}
                            {!isRedirecting && (
                                <Grid size={12} className="form-actions">
                                    {isChangePasswordMode ? (
                                        <LinkButton to="/settings">Back to settings</LinkButton>
                                    ) : (
                                        <>
                                            <LinkButton to="/sign-in">Back to sign in</LinkButton>
                                            <LinkButton to="/forgot-password">Resend forgot password link</LinkButton>
                                        </>
                                    )}
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </Box>
        </BasePage>
    );
}